import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Skeleton,
  Typography,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material'
import { Add, Delete, InsertDriveFile } from '@mui/icons-material'
import { Form, TextInput, required, useTranslate } from 'react-admin'
import dayjs from 'dayjs'
import { blue } from 'src/assets/colors'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { LoadingButton } from '@mui/lab'
import { DatePickers } from 'src/components/uiElements/react-hook-fields'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { billingServices } from 'src/services/reservation/billing/billing.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'

export const GenerateInvoiceModal = ({ open, onClose, onGenerate, conciliation, submitting = false }) => {
  const translate = useTranslate()
  const [loadingBasesList, setLoadingBasesList] = useState(false)
  const [basesList, setBasesList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)
  const [currencyList, setCurrencyList] = useState([])
  const [fileToUpload, setFileToUpload] = useState(null)

  const documenTypeOptions = ['invoice', 'debit_note', 'credit_note']

  useEffect(() => {
    if (open) {
      getBases()
      getCurrencies()
      setFileToUpload(null)
    }
  }, [open])

  const getBases = async () => {
    try {
      setLoadingBasesList(true)
      const res = await billingServices.getBillingBases()
      const baseArray = Object.entries(res?.options ?? [])
      setBasesList(
        baseArray
          .map((base) => ({ name: base[0], id: base[1] }))
          .filter((base, index, self) => self.findIndex((elem) => elem.id === base.id) === index)
      )
    } catch (error) {
      setBasesList([])
    } finally {
      setLoadingBasesList(false)
    }
  }

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices.getByAll()
      setCurrencyList(allCurrencies)
    } catch (error) {
      setCurrencyList([])
    } finally {
      setLoadingCurrencyList(false)
    }
  }

  const handleDownloadFile = () => {
    if (!fileToUpload) return
    const url = URL.createObjectURL(fileToUpload)
    const link = document.createElement('a')
    link.href = url
    link.download = fileToUpload.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const handleClickDeleteFile = (event) => {
    event.stopPropagation()
    setFileToUpload(null)
  }

  const defaultCurrencyId = conciliation?.currency_id || conciliation?.currency_relation?.id

  const defaultValues = {
    document_type: 'invoice',
    document_number: '',
    currency_id: defaultCurrencyId,
    base: '',
    date: null,
    due_date: dayjs(),
    accounting_date: dayjs(),
  }

  const handleSubmit = (data) => {
    const formData = {
      ...data,
      fileToUpload,
      basesList,
    }
    if (onGenerate) {
      onGenerate(formData)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ style: { maxWidth: '1125px', width: '100%' } }}
    >
      <DialogTitle>{translate('ra.title.voucher_details') || 'Datos del comprobante'}</DialogTitle>
      {open && (
        <Form defaultValues={defaultValues} onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <EditInputsWrap className='small' label={translate('ra.field.document_type')} required lg={3}>
                <SelectInput
                  source='document_type'
                  fullWidth
                  options={documenTypeOptions.map((option) => ({
                    id: option,
                    name: translate(`ra.field.${option}`),
                  }))}
                  optionsText={['name']}
                  label=''
                  validate={[required()]}
                />
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.document_number')} required lg={3}>
                <TextInput source='document_number' fullWidth label='' validate={[required()]} />
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.currency')} required lg={3}>
                {loadingCurrencyList ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput
                    size='small'
                    fullWidth
                    label={false}
                    source='currency_id'
                    options={currencyList}
                    optionsText={['long_name']}
                    getOptionLabel={(item) => `${item.symbol} ${item.long_name} (${item.short_name})`}
                    validate={[required()]}
                  />
                )}
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.billing_base')} lg={3}>
                {loadingBasesList ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput source='base' fullWidth options={basesList} optionsText={['name']} label={false} />
                )}
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.voucher_date')} required lg={4}>
                <DatePickers
                  source='date'
                  viewsShow={false}
                  defaultHour={0}
                  defaultMinute={0}
                  style={{ width: '100%' }}
                  required
                />
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.due_date')} required lg={4}>
                <DatePickers
                  source='due_date'
                  viewsShow={false}
                  defaultHour={0}
                  defaultMinute={0}
                  style={{ width: '100%' }}
                  required
                  defaultValue={dayjs()}
                />
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.accounting_date')} required lg={4}>
                <DatePickers
                  source='accounting_date'
                  viewsShow={false}
                  defaultHour={0}
                  defaultMinute={0}
                  style={{ width: '100%' }}
                  required
                  defaultValue={dayjs()}
                />
              </EditInputsWrap>

              <EditInputsWrap className='small' label={translate('ra.field.file')} lg={4}>
                {fileToUpload ? (
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1}
                    sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1, cursor: 'pointer' }}
                    onClick={() => handleDownloadFile()}
                  >
                    <InsertDriveFile color='primary' />
                    <Tooltip title={fileToUpload.name}>
                      <Typography noWrap variant='body2' style={{ flexGrow: 1 }}>
                        {fileToUpload.name}
                      </Typography>
                    </Tooltip>
                    <IconButton size='small' onClick={(e) => handleClickDeleteFile(e)}>
                      <Delete color='error' />
                    </IconButton>
                  </Box>
                ) : (
                  <Button variant='standard' startIcon={<Add />} style={{ color: blue }} component='label'>
                    {translate('ra.action.add_file')}
                    <input
                      type='file'
                      hidden
                      inputProps={{ multiple: false }}
                      onClick={(e) => (e.target.value = null)}
                      onChange={(e) => setFileToUpload(e.target.files?.[0])}
                    />
                  </Button>
                )}
              </EditInputsWrap>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} variant='outlined' style={{ textTransform: 'none' }} disabled={submitting}>
              {translate('ra.action.cancel') || 'Cancelar'}
            </Button>
            <LoadingButton
              type='submit'
              variant='contained'
              color='primary'
              loading={submitting}
              disabled={submitting}
              style={{ textTransform: 'none' }}
            >
              {translate('ra.action.generate') || 'Generar'}
            </LoadingButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  )
}
